/**
 * 1.4 P25: public customer-invite acceptance (`/customer-invite/<token>`).
 * Peeks the invite, lets the imported customer set a password, signs them
 * in, then points them at the card step their membership needs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Button from '../../src/components/Button';
import Input from '../../src/components/Input';
import ContentContainer from '../../src/components/ContentContainer';
import { api, ApiError } from '../../src/api/client';
import { useAuth } from '../../src/context/AuthContext';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, type ColorTheme } from '../../src/utils/theme';

interface InvitePeek { email: string | null; firstName: string | null; washName: string | null; planName: string | null; priceMonthlyCents?: number | null; nextBillDate?: string | null; hasCard?: boolean; setupState: string | null; cardRequiredBy: string | null; kind: 'set_password' | 'plan_ready'; expired: boolean; accepted: boolean }
interface RespondResponse { success: boolean; decision: 'accept' | 'decline'; setupState: string; hasCard: boolean; billingStarts: string | null }
interface AcceptResponse { token?: string; refreshToken?: string; role: 'user'; profile: { id: string; email: string; name: string }; setupState: string | null; cardRequiredBy: string | null; washName: string | null; planName: string | null }

export default function CustomerInviteScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const router = useRouter();
  const { completeVerification } = useAuth();
  const { token } = useLocalSearchParams<{ token?: string }>();
  const [peek, setPeek] = useState<InvitePeek | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState(false);
  const [done, setDone] = useState<AcceptResponse | null>(null);
  const [planDone, setPlanDone] = useState<RespondResponse | null>(null);

  useEffect(() => {
    if (!token) { setError('Missing invite token'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const p = await api<InvitePeek>(`/api/public/customer-invites/${encodeURIComponent(token)}/peek`, { skipAuth: true });
        if (!cancelled) setPeek(p);
      } catch (e: any) {
        if (!cancelled) setError(e instanceof ApiError ? (e.message || 'Invite not found') : 'Invite not found');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError(null); setSubmitting(true);
    try {
      const res = await api<AcceptResponse>(`/api/public/customer-invites/${encodeURIComponent(token!)}`, { method: 'POST', body: { password }, skipAuth: true });
      await completeVerification(res.token ?? '', res.refreshToken ?? '', 'user', { ...res.profile, emailVerified: true } as any);
      setDone(res);
    } catch (e: any) {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === 'INVITE_EXPIRED') setPeek(p => (p ? { ...p, expired: true } : p));
      setError(e instanceof ApiError ? (e.message || 'Could not finish setup') : 'Could not finish setup');
    } finally { setSubmitting(false); }
  };

  // Nathan 10-18: an existing account accepts or declines the plan the owner attached. Nothing is billed before accept.
  const respond = async (decision: 'accept' | 'decline') => {
    setError(null); setSubmitting(true);
    try {
      const res = await api<RespondResponse>(`/api/public/customer-invites/${encodeURIComponent(token!)}/respond`, { method: 'POST', body: { decision }, skipAuth: true });
      setPlanDone(res);
    } catch (e: any) {
      const code = e instanceof ApiError ? e.code : undefined;
      if (code === 'INVITE_EXPIRED') setPeek(p => (p ? { ...p, expired: true } : p));
      setError(e instanceof ApiError ? (e.message || 'Could not record your answer') : 'Could not record your answer');
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    setSubmitting(true); setError(null);
    try { await api(`/api/public/customer-invites/${encodeURIComponent(token!)}/resend`, { method: 'POST', body: {}, skipAuth: true }); setResent(true); }
    catch (e: any) { setError(e instanceof ApiError ? e.message : 'Could not send a new link'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : null);

  return (
    <>
      <Stack.Screen options={{ title: peek?.washName ? `${peek.washName} on Rinsepay` : 'Finish setup' }} />
      <View style={styles.center}>
        <ContentContainer maxWidth={460}>
          <View style={styles.card}>
            {done ? (
              <>
                <Ionicons name="checkmark-circle" size={56} color={colors.success} style={styles.icon} />
                <Text style={styles.title}>You're all set{done.profile.name ? `, ${done.profile.name.split(' ')[0]}` : ''}</Text>
                {done.setupState === 'pending_setup' ? (
                  <>
                    <Text style={styles.body}>One last step: add a card so your {done.planName ?? 'membership'} can bill. Washes stay locked until a card is on file.</Text>
                    <Button title="Add a card" onPress={() => router.replace('/payment-methods?returnTo=/user/subscriptions' as never)} style={styles.btn} />
                  </>
                ) : (
                  <>
                    <Text style={styles.body}>Your {done.planName ?? 'membership'} is active now.{done.cardRequiredBy ? ` Add a card by ${fmtDate(done.cardRequiredBy)} to keep it going.` : ''}</Text>
                    <Button title="Add a card" onPress={() => router.replace('/payment-methods?returnTo=/user/subscriptions' as never)} style={styles.btn} />
                    <Button title="Skip for now" variant="outline" onPress={() => router.replace('/' as never)} style={styles.btn} />
                  </>
                )}
                <View style={styles.steps}>
                  <Text style={styles.stepsTitle}>At the wash</Text>
                  <Text style={styles.step}>1. Open the Rinsepay app</Text>
                  <Text style={styles.step}>2. Scan the QR code at the bay</Text>
                  <Text style={styles.step}>3. Pick your wash</Text>
                </View>
              </>
            ) : !peek ? (
              <>
                <Ionicons name="alert-circle-outline" size={56} color={colors.danger} style={styles.icon} />
                <Text style={styles.title}>Invite not found</Text>
                <Text style={styles.body}>{error || 'This link is not valid.'}</Text>
                <Button title="Go to Rinsepay" onPress={() => router.replace('/' as never)} style={styles.btn} />
              </>
            ) : peek.accepted ? (
              <>
                <Ionicons name="checkmark-circle-outline" size={56} color={colors.primary} style={styles.icon} />
                <Text style={styles.title}>Already set up</Text>
                <Text style={styles.body}>This link was already used. Sign in with {peek.email} and your password.</Text>
                <Button title="Sign in" onPress={() => router.replace('/login' as never)} style={styles.btn} />
              </>
            ) : peek.expired ? (
              <>
                <Ionicons name="time-outline" size={56} color={colors.warning} style={styles.icon} />
                <Text style={styles.title}>This link has expired</Text>
                <Text style={styles.body}>{resent ? `A new link is on its way to ${peek.email}.` : 'Invite links last 14 days. We can send you a fresh one.'}</Text>
                {!resent && <Button title="Send me a new link" onPress={resend} loading={submitting} style={styles.btn} />}
                {!!error && <Text style={styles.error}>{error}</Text>}
              </>
            ) : planDone ? (
              <>
                <Ionicons name={planDone.decision === 'accept' ? 'checkmark-circle-outline' : 'close-circle-outline'} size={56} color={planDone.decision === 'accept' ? colors.success : colors.textSecondary} style={styles.icon} />
                <Text style={styles.title}>{planDone.decision === 'accept' ? 'Membership accepted' : 'No changes made'}</Text>
                <Text style={styles.body}>
                  {planDone.decision === 'decline'
                    ? `${peek.washName ?? 'The car wash'} will not add this membership to your account.`
                    : planDone.hasCard
                      ? `Billing starts ${fmtDate(planDone.billingStarts) ?? 'on your next bill date'} on the card you have on file. Sign in to use your membership.`
                      : 'Sign in and add a card to finish. Nothing is billed until then.'}
                </Text>
                <Button title="Sign in" onPress={() => router.replace('/login' as never)} style={styles.btn} />
              </>
            ) : peek.kind === 'plan_ready' ? (
              <>
                <Ionicons name="car-sport-outline" size={56} color={colors.primary} style={styles.icon} />
                <Text style={styles.title}>{peek.washName ?? 'A car wash'} wants to add you to {peek.planName ?? 'a membership'}</Text>
                <Text style={styles.body}>
                  {peek.priceMonthlyCents != null ? `$${(peek.priceMonthlyCents / 100).toFixed(2)} per month` : 'Monthly membership'}
                  {peek.nextBillDate ? `, first bill ${fmtDate(peek.nextBillDate) ?? peek.nextBillDate}` : ''}, on your Rinsepay account ({peek.email}).
                  {' '}Nothing is billed until you accept{peek.hasCard ? '' : ' and add a card'}.
                </Text>
                {error && <Text style={styles.error}>{error}</Text>}
                <Button title="Accept membership" onPress={() => respond('accept')} loading={submitting} size="lg" style={styles.btn} />
                <Button title="No thanks" variant="outline" onPress={() => respond('decline')} loading={submitting} style={styles.btn} />
                <Text style={styles.fine}>By accepting you agree to the Rinsepay terms and privacy policy.</Text>
              </>
            ) : (
              <>
                <Ionicons name="water" size={56} color={colors.primary} style={styles.icon} />
                <Text style={styles.title}>Welcome to {peek.washName ?? 'Rinsepay'}</Text>
                <Text style={styles.body}>
                  {peek.firstName ? `Hi ${peek.firstName} — ` : ''}set a password for <Text style={{ fontWeight: '700' as any }}>{peek.email}</Text>{peek.planName ? ` and your ${peek.planName} membership is ready to use` : ''}.
                </Text>
                <Input label="Password" value={password} onChangeText={setPassword} secureTextEntry autoCapitalize="none" placeholder="At least 8 characters" />
                <Input label="Confirm password" value={confirm} onChangeText={setConfirm} secureTextEntry autoCapitalize="none" />
                {!!error && <Text style={styles.error}>{error}</Text>}
                <Button title="Set password" onPress={accept} loading={submitting} size="lg" style={styles.btn} />
                <Text style={styles.fine}>By continuing you accept the Rinsepay terms and privacy policy.</Text>
              </>
            )}
          </View>
        </ContentContainer>
      </View>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  center: { flex: 1, backgroundColor: c.background, justifyContent: 'center', padding: spacing.lg },
  card: { backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.xl, borderWidth: 1, borderColor: c.borderLight },
  icon: { alignSelf: 'center', marginBottom: spacing.sm },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, textAlign: 'center', marginBottom: spacing.sm },
  body: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: spacing.md },
  btn: { marginTop: spacing.sm },
  error: { color: c.danger, fontSize: fontSize.sm, textAlign: 'center', marginTop: spacing.xs },
  fine: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginTop: spacing.md },
  steps: { marginTop: spacing.lg, padding: spacing.md, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md },
  stepsTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  step: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 20 },
});
